import { FloatingHint } from "@/components/FloatingHint";
import { useMemo, useState, useEffect, useCallback } from "react";
import { BannerProgressHint } from "@/components/BannerProgressHint";
import {
    useGetInferenceQueueItemById,
    InferenceStatus,
    useCurrentMedia,
    useCurrentWorkspace,
    useCurrentWorkspaceApiKey,
    useCurrentWorkflow
} from "@/store";
import { MediaItemUuid } from "@/types/media";
import { useStopGeneratingPredictions } from "@/hooks/useStopGeneratingPredictions";
import { useGetPredictions } from "@/hooks/useGetPredictions";
import { useFlow } from "@/contexts/FlowContext";
import { useFilePreview } from "@/contexts/FilePreviewContext";
import { useFlowState } from "@/hooks/useFlowState";
import { Flows } from "@/types/flows";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";

export const InferenceProgressHint = ({
    fileId,
    rotatingContent
}: {
    fileId: MediaItemUuid;
    rotatingContent?: string[];
}) => {
    const inferenceQueueItem = useGetInferenceQueueItemById(fileId);
    const { stopGeneratingPredictions } = useStopGeneratingPredictions();
    const { generateModel, getPredictionsForFile } = useGetPredictions();
    const currentMedia = useCurrentMedia();
    const { videoControls } = useFilePreview();
    const { flow } = useFlowState();
    const workflow = useCurrentWorkflow();
    const apiKey = useCurrentWorkspaceApiKey();
    const [lastProgress, setLastProgress] = useState(0);
    const [showHint, setShowHint] = useState(false);
    const [isStopped, setIsStopped] = useState(false);

    // Track if we ever had an inference item
    useEffect(() => {
        if (inferenceQueueItem) {
            setShowHint(true);
            setLastProgress(inferenceQueueItem.progress || 0);
            // If we have new inference data, we're running again
            setIsStopped(false);
        }
    }, [inferenceQueueItem]);

    // Hide when completed
    useEffect(() => {
        if (inferenceQueueItem?.status === InferenceStatus.COMPLETED) {
            setShowHint(false);
            setIsStopped(false);
        }
    }, [inferenceQueueItem?.status]);

    const handleStop = useCallback(() => {
        setIsStopped(true);
        stopGeneratingPredictions();

        // Pause video directly
        if (videoControls?.videoRef?.current) {
            const videoElement = videoControls.videoRef.current;
            const currentTime = videoElement.currentTime;
            console.log("[InferenceProgressHint] Pausing video at time:", currentTime);

            // Directly pause the video element
            videoElement.pause();

            // Also call the pause callback to update state
            videoControls.onPauseClicked(currentTime);
        }
    }, [stopGeneratingPredictions, videoControls]);

    const handleStart = useCallback(async () => {
        setIsStopped(false);

        // Resume video if it was paused
        if (videoControls?.videoRef?.current) {
            const videoElement = videoControls.videoRef.current;
            console.log("[InferenceProgressHint] Resuming video");

            // Directly play the video element
            videoElement.play().catch((err) => {
                console.error("[InferenceProgressHint] Failed to play video:", err);
            });

            // Also call the play callback to update state
            if (videoControls.onPlayClicked) {
                videoControls.onPlayClicked();
            }
        }

        try {
            // Different restart logic based on flow
            if (flow === Flows.DEPLOY || flow === Flows.SHARE) {
                // For deploy/share flows, use workflow inference directly
                if (!currentMedia?.file) {
                    console.warn("[InferenceProgressHint] No current media file");
                    return;
                }

                console.log("[InferenceProgressHint] Restarting workflow inference");
                const abortController = new AbortController();

                const inferenceType =
                    flow === Flows.DEPLOY
                        ? InferenceTypes.WORKFLOW
                        : InferenceTypes.WORKFLOW_PUBLIC;

                await getPredictionsForFile({
                    mediaItem: currentMedia.file,
                    abortController,
                    useInferenceQueue: true,
                    inferenceType
                });
            } else {
                // For build flow, use generateModel (requires annotations)
                console.log("[InferenceProgressHint] Restarting model generation");
                await generateModel();
            }
        } catch (error) {
            console.error("Failed to restart predictions:", error);
        }
    }, [generateModel, getPredictionsForFile, videoControls, flow, currentMedia, workflow, apiKey]);

    const hint = useMemo(() => {
        if (showHint) {
            const props: {
                progress: number;
                onStop: () => void;
                onRestart: () => void;
                rotatingContent?: string[];
                isStopped: boolean;
            } = {
                progress: inferenceQueueItem?.progress || lastProgress,
                onStop: handleStop,
                onRestart: handleStart,
                isStopped
            };

            if (rotatingContent) {
                props.rotatingContent = rotatingContent;
            }

            return <BannerProgressHint {...props} />;
        }
        return null;
    }, [
        showHint,
        inferenceQueueItem?.progress,
        lastProgress,
        rotatingContent,
        isStopped,
        handleStop,
        handleStart
    ]);

    if (!hint) return null;

    return (
        <FloatingHint
            isVisible={!!hint}
            message={hint || ""}
            isDismissable={false}
            lightMode={true}
        />
    );
};
